import { useEntity } from '@/hooks/queries/useEntityQuery';
import { panelServiceAdapter } from '@/services/adapters/panelServiceAdapter';
import { Panel } from '@/types/Panel';
import { useMemo } from 'react';

export function usePanelsStandardized(floorPlanId: string) {
  const { data, isLoading, error, refetch, create, update, delete: deletePanel, ...mutations } = useEntity<Panel>(
    'panels',
    floorPlanId,
    panelServiceAdapter
  );

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!data) return { totalPanels: 0, totalSpaces: 0 };

    const totalPanels = data.length;
    const totalSpaces = data.reduce((sum, panel: any) => sum + (panel.num_spaces || 0), 0);

    return { totalPanels, totalSpaces };
  }, [data]);

  // Enhanced create function with floor plan assignment
  const createPanel = async (panelData: any) => {
    const panelWithFloorPlan = {
      ...panelData,
      floor_plan_id: floorPlanId,
    };
    return create(panelWithFloorPlan);
  };

  // Enhanced update function
  const updatePanel = async (id: string, updates: any) => {
    return update({ id, updates });
  };

  // Bulk operations
  const updatePanelPositions = async (updates: { id: string; x: number; y: number }[]) => {
    const promises = updates.map(({ id, x, y }) => 
      updatePanel(id, { x_position: x, y_position: y })
    );
    return Promise.all(promises);
  };

  const updatePanelSpaces = async (updates: { id: string; num_spaces: number }[]) => {
    const promises = updates.map(({ id, num_spaces }) => 
      updatePanel(id, { num_spaces })
    );
    return Promise.all(promises);
  };

  return {
    // Data
    panels: data || [],
    
    // Loading states
    isLoading,
    error,
    
    // Mutation states
    isCreating: mutations.isCreating,
    isUpdating: mutations.isUpdating,
    isDeleting: mutations.isDeleting,
    
    // Operations
    createPanel,
    updatePanel,
    deletePanel,
    refetch,
    
    // Bulk operations
    updatePanelPositions,
    updatePanelSpaces,
    
    // Statistics
    totalPanels: statistics.totalPanels,
    totalSpaces: statistics.totalSpaces,
  };
} 